<template>
  <div class="flex flex-col gap-y-1">
    <label class="w-full flex flex-col gap-0.5">
      <div
        :class="direction"
        class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary flex-wrap"
      >
        <div v-if="label" :class="labelClass" class="flex w-1/2 items-center">
          {{ t9nLabel }} <span v-if="isRequired" class="text-brand-red">*</span>
        </div>
        <div v-tippy="popoverContent" class="w-full flex flex-col gap-y-2">
          <slot name="topAppendage"></slot>
          <VDatePicker
            :min-date="parsedMinDate"
            :max-date="parsedMaxDate"
            :masks="masks"
            v-model.string="dateValue"
          >
            <template #default="{ inputValue, inputEvents, togglePopover }">
              <div class="w-full relative">
                <input
                  v-maska
                  :data-maska="parsedMask?.mask"
                  :data-maska-tokens="parsedMask?.tokens"
                  :data-maska-reversed="parsedMask?.reversed"
                  :class="[{ 'border-s-4 border-s-primary': isRequired }]"
                  :value="inputValue"
                  :disabled="withIsDisabled"
                  :placeholder="serverDateFormat"
                  class="w-full ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary read-only:bg-brand-gray-250 placeholder:normal-case disabled:cursor-not-allowed"
                  v-on="inputEvents"
                />
                <div
                  class="border-s border-x-gray-300 absolute top-0 end-0 h-full flex items-center gap-x-4 px-3"
                  @click.prevent="handleCalendarIcon(togglePopover)"
                >
                  <button>
                    <!-- <FontAwesomeIcon :icon="faCalendarAlt" class="text-brand-black-secondary" /> -->
                    <faCalendar class="w-3.5 fill-brand-black-body" />
                  </button>
                </div>
              </div>
            </template>
          </VDatePicker>
          <slot name="bottomAppendage"></slot>
        </div>
      </div>
    </label>

    <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
      <span :key="err" v-for="err in errors" class="text-brand-red">
        {{ $t(err) }}
      </span>
    </div>
  </div>
</template>

<script setup>
import faCalendar from '@/assets/icons/faCalendar.svg?component'
import faTimes from '@/assets/icons/faTimes.svg?component'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { vMaska } from 'maska'

const emit = defineEmits('onChange')
/**
 * DO NOT ADD DEFAULT_VALUE.
 *
 * use field.value to set the default value for this component
 */
const props = defineProps({
  label: String,
  field: Object,
  errors: {
    type: Array,
    default: () => []
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  serverDateFormat: {
    type: String,
    default: () => 'dd/MM/yyyy'
  },
  minDate: [Object, String, Date],
  maxDate: [Object, String, Date],
  popoverContent: [Object, String],
  isRequired: {
    type: Boolean,
    default: () => false
  },
  disabled: Boolean
})

const i18n = useI18n()

const dateValue = ref(props.field.value)
const masks = ref({
  modelValue: props.serverDateFormat.toUpperCase(),
  input: props.serverDateFormat.toUpperCase()
})

const t9nLabel = computed(() => i18n.t(props.label))
const t9nPlaceholder = computed(() => props.placeholder && i18n.t(props.placeholder))

const parsedMask = computed(() => {
  return {
    mask: '99/99/9999',
    tokens: '9:[0-9]:repeated',
    reversed: true
  }
})

const parsedMinDate = computed(() => {
  if (props.minDate == '__now__') {
    return new Date()
  }

  if (typeof props.minDate == 'string') {
    return DateTime.fromFormat(props.minDate, props.serverDateFormat)
  }

  return props.minDate
})

const parsedMaxDate = computed(() => {
  if (props.maxDate == '__now__') {
    return new Date()
  }

  if (typeof props.minDate == 'string') {
    return DateTime.fromFormat(props.maxDate, props.serverDateFormat)
  }

  return props.maxDate
})

watch(dateValue, (newDate) => emit('onChange', newDate))

const handleCalendarIcon = (togglePopover) => {
  // if (props.is) return
  togglePopover()
}
</script>

<style lang="scss" scoped></style>
